import { SelectOption } from "@veneer/core";
import { useCallback, useEffect, useState } from "react";
import {
  IPostInputs,
  IPostInputsSave,
  IPostInputsUpdate,
} from "../../../models/dto/application/IPostInputs";
import publisherService from "../../../services/generalApi/publisher.service";
import { IFieldDto } from "../../../models/dto/surveyApi/IFieldDto";
import moment from "moment";
import productService from "../../../services/generalApi/product.service";
import { useSelector } from "react-redux";
import { RootState } from "../../../app/store";
import { decryptText } from "../../../utils/encryptionUtils";

// Initial state for form input values
const initialStateInput: IPostInputs = {
  title: "",
  description: "",
  video: "",
  url: "",
  subregionValues: [],
  roleValues: [],
  pdfUrl: "",
  imageUrl: "",
};

/**
 * Custom Hook `usePublisherModalViewModel`
 *
 * This hook manages the logic and state for a publishing modal in the application.
 * It includes functions to handle input values, button validation, file uploads,
 * fetching options for dropdowns, and creating or updating posts.
 *
 * @returns {Object} - A set of states and functions to interact with the publishing modal.
 */
export const usePublisherModalViewModel = () => {
  const [inputValues, setInputValues] = useState<IPostInputs>(initialStateInput);
  const [isButtonValid, setIsButtonValid] = useState<boolean>(false);
  const [subregionOptions, setSubregionOptions] = useState<SelectOption<number>[]>([]);
  const [roleOptions, setRoleOptions] = useState<SelectOption<number>[]>([]);
  const [loadingFile, setLoadingFile] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [createStatus, setCreateStatus] = useState<boolean | null>(null);
  const [userId, setUserId] = useState<number>(0);
  const user: any = useSelector((state: RootState) => state.user);
  const userIdLogged = Number(decryptText(user.user?.userId));

  // Effect to fetch subregion options when the component mounts.
  useEffect(() => {
    getSubretailAsync();
    getRoleAsync();
  }, []);

  // Effect to validate the save button when input values or file upload status changes.
  useEffect(() => {
    handleButtonValidation();
    // eslint-disable-next-line
  }, [inputValues, loadingFile]);

  /**
   * Function to handle changes in form input values.
   *
   * @param {keyof IPostInputs} field - The input field being updated.
   * @param {string | number[]} value - The new value for the input field.
   */
  const handleInputValues = (
    field: keyof IPostInputs,
    value: string | number[]
  ) => {
    setInputValues((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  /**
   * Function to handle changes in the subregion dropdown selection.
   *
   * @param {SelectOption<number>} selectedOption - The selected option from the subregion dropdown.
   */
  const onChangeSubregion = (selectedOption: SelectOption<number>) => {
    const valueCopy = [...inputValues.subregionValues];
    const isOptionSelected = valueCopy.includes(selectedOption.value);
    if (isOptionSelected) {
      const index = valueCopy.indexOf(selectedOption.value);
      valueCopy.splice(index, 1);
    } else {
      valueCopy.push(selectedOption.value);
    }
    handleInputValues("subregionValues", valueCopy);
  };

  const onClearSubregion = () => handleInputValues("subregionValues", []);

  /**
   * Function to handle changes in the role dropdown selection.
   *
   * @param {SelectOption<number>} selectedOption - The selected option from the role dropdown.
   */
  const onChangeRole = (selectedOption: SelectOption<number>) => {
    const valueCopy = [...inputValues.roleValues];
    const isOptionSelected = valueCopy.includes(selectedOption.value);
    if (isOptionSelected) {
      const index = valueCopy.indexOf(selectedOption.value);
      valueCopy.splice(index, 1);
    } else {
      valueCopy.push(selectedOption.value);
    }
    handleInputValues("roleValues", valueCopy);
  };

  const onClearRole = () => handleInputValues("roleValues", []);

  /**
   * Async function to fetch the list of subregions from the API.
   */
  const getSubretailAsync = async () => {
    try {
      const response: IFieldDto[] = await publisherService.getSubretailList();
      const options = response.map((item) => ({
        value: item.fieldID,
        label: item.fieldValue,
      }));
      setSubregionOptions(options);
    } catch (error) {
      console.error(error);
      // TODO: set the default value
    }
  };

  /**
   * Async function to fetch the list of roles from the API.
   */
  const getRoleAsync = async () => {
    try {
      const response: IFieldDto[] = await publisherService.getRoleList(2);
      const options = response.map((item) => ({
        value: item.fieldID,
        label: item.fieldValue,
      }));
      setRoleOptions(options);
    } catch (error) {
      console.error(error);
      // TODO: set the default value
    }
  };

  /**
   * Function to handle file changes and uploads.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event - The file input change event.
   * @param {FileList | []} files - The selected files.
   * @param {string} inputValue - The current value of the input.
   * @param {keyof IPostInputs} field - The field to update with the uploaded file URL.
   */
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    files: FileList | [],
    inputValue: string,
    field: keyof IPostInputs
  ) => {
    if (files.length > 0) {
      const formData = new FormData();
      formData.append("formFile", files[0]);

      const folderName = "Post";
      const currentDate = moment().format("YYYY-MM-DD");

      try {
        setLoadingFile(true);
        const response = await productService.postImage(
          formData,
          folderName,
          moment(currentDate).format("YYYY-MM-DD")
        );
        setInputValues((prevState) => ({
          ...prevState,
          [field]: response.path,
        }));
      } catch (error) {
        console.error("Error al subir el archivo", error);
      } finally {
        setLoadingFile(false);
      }
    }
  };

  /**
   * Function to clear the file selected in the file picker.
   *
   * @param {keyof IPostInputs} field - The field to clear.
   */
  const onClearFilePicker = (field: keyof IPostInputs) => {
    setInputValues((prevState) => ({
      ...prevState,
      [field]: "",
    }));
  };

  /**
   * Function to validate if the save button should be enabled.
   */
  const handleButtonValidation = useCallback(() => {
    const { title, description, subregionValues, roleValues } = inputValues;
    const isTitleValid = title.length > 0;
    const isDescriptionValid = description.length > 0;
    const isSubregionValid = subregionValues.length > 0;
    const isRoleValid = roleValues.length > 0;
    const isLoadingFile = loadingFile;
    setIsButtonValid(
      isTitleValid &&
        isDescriptionValid &&
        isSubregionValid &&
        isRoleValid &&
        !isLoadingFile
    );
  }, [inputValues, loadingFile]);

  /**
   * Async function to save a new post.
   */
  const handleSavePost = useCallback(async () => {
    try {
      setLoading(true);
      const post: IPostInputsSave = {
        text: inputValues.description,
        postTitle: inputValues.title,
        imageURL: inputValues.imageUrl || "",
        videoURL: inputValues.video || "",
        pdfURL: inputValues.pdfUrl || "",
        url: inputValues.url || "",
        roleIDs: inputValues.roleValues.join(",") || "",
        subregions: inputValues.subregionValues.join(",") || "",
      };

      const response = await publisherService.postCreatePost(post);

      if (response === 1) {
        setCreateStatus(true);
      } else {
        setCreateStatus(false);
      }
    } catch (error) {
      console.error("Error al crear la publicación", error);
    } finally {
      setLoading(false);
      setInputValues(initialStateInput);
    }
  }, [inputValues]);

  /**
   * Async function to update an existing post.
   *
   * @param {number} postID - The ID of the post to update.
   */
  const handleUpdatePost = useCallback(
    async (postID: number) => {
      try {
        setLoading(true);
        const post: IPostInputsUpdate = {
          postID: postID,
          text: inputValues.description,
          postTitle: inputValues.title,
          imageURL: inputValues.imageUrl || "",
          videoURL: inputValues.video || "",
          pdfURL: inputValues.pdfUrl || "",
          url: inputValues.url || "",
          roleIDs: inputValues.roleValues.join(",") || "",
          subregions: inputValues.subregionValues.join(",") || "",
        };

        const response = await publisherService.putPostUpdate(post);

        if (response === 1) {
          setCreateStatus(true);
        } else {
          setCreateStatus(false);
        }
      } catch (error) {
        console.error("Error al crear la publicación", error);
      } finally {
        setLoading(false);
        setInputValues(initialStateInput);
      }
    },
    [inputValues]
  );

  /**
   * Function to close the creation status notification.
   */
  const handleCloseCreateStatus = () => {
    setCreateStatus(null);
  };

  /**
   * Async function to fetch post data for updating.
   *
   * @param {number} postID - The ID of the post to update.
   */
  const getPublisherDataToUpdate = async (postID: number) => {
    try {
      setLoading(true);
      const response = await publisherService.getPostToUpdate(postID);
      const subregionValues = response.subregionID.split(",").map(Number);
      const roleValues = response.roleID.split(",").map(Number);
      setUserId(response.userID);
      setInputValues({
        title: response.postTitle,
        description: response.text,
        imageUrl: response.imageURL,
        video: response.videoURL,
        url: response.url,
        pdfUrl: response.pdfURL,
        subregionValues,
        roleValues,
      });
    } catch (error) {
      console.error("Error al obtener los datos de la publicación", error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Function to clear all form input values.
   */
  const cleanInputValues = () => {
    setInputValues(initialStateInput);
  };

  return {
    inputValues,
    isButtonValid,
    subregionOptions,
    roleOptions,
    loading,
    createStatus,
    userId,
    userIdLogged,
    onChangeSubregion,
    onClearSubregion,
    onChangeRole,
    onClearRole,
    handleInputValues,
    handleFileChange,
    onClearFilePicker,
    handleSavePost,
    handleCloseCreateStatus,
    getPublisherDataToUpdate,
    handleUpdatePost,
    cleanInputValues,
  };
};
